import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { debounceTime, distinctUntilChanged, map, take } from 'rxjs/operators';
import { QuillEditorComponent } from 'ngx-quill';
import * as Quill from 'quill';

import {
  mjTextEditorFinnModules,
  mjTextEditorH1Modules,
  mjTextEditorH2Modules,
  mjTextEditorH3Modules,
  mjTextEditorPModules,
} from './mj-text-editor.config';
import { FormBuilder, FormGroup } from '@angular/forms';

const emptyArray: any[] = [];

export interface EditorText {
  rich: string;
  plain: string;
}

@Component({
  selector: 'mj-text-editor',
  templateUrl: './mj-text-editor.component.html',
  styleUrls: ['./mj-text-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MjTextEditorComponent implements OnChanges, OnDestroy {
  @Input() plainText: string;
  @Input() richText: string;
  @Input() required = false;
  @Input() fontColor: string = '#000000';
  @Input() textMaxLength: number = 5000;
  @Input() textarea: boolean = false;
  @Input() hideMaxLength: boolean = false;
  @Input() placeholder: string;
  @Input() type: string = 'p';
  @Input() format: 'json' | 'html' = 'json';
  @Input() readOnly: boolean = false;

  @ViewChild('editor', { static: false }) editor: QuillEditorComponent;

  @Output() onTextUpdated = new EventEmitter<EditorText>();

  counter = 0;
  shouldPresentPlaceholder = true;
  placeholderClass: string;
  errorMsg = '';
  error = false;

  formSub$: any;
  form: FormGroup;
  showEditor: boolean = false;
  toolbar;

  constructor(
    private fb: FormBuilder,
    private ref: ChangeDetectorRef,
    private _snackbar: MatSnackBar,
    private _translate: TranslateService
  ) {
    this._initForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.plainText &&
      this.plainText &&
      this.plainText !== this.formValue.plainText
    ) {
      this.form.patchValue({
        plainText: this.plainText,
      });
    }

    if (changes.richText && this.richText !== this.formValue.richText) {
      this.form.patchValue({
        richText: this.richText,
      });
    }
  }

  private _initForm() {
    // this.showEditor = false;
    switch (this.type) {
      case 'h1':
        this.toolbar = mjTextEditorH1Modules;
        break;
      case 'h2':
        this.toolbar = mjTextEditorH2Modules;
        break;
      case 'h3':
        this.toolbar = mjTextEditorH3Modules;
        break;
      case 'p':
        this.toolbar = mjTextEditorPModules;
        break;
      case 'finn':
        this.toolbar = mjTextEditorFinnModules;
        break;
    }
    this.form = this.fb.group(
      {
        richText: '',
        plainText: '',
      },
      { updateOn: 'change' }
    );

    this._onChanges();
  }

  get counterValue() {
    return this.counter - 1;
  }

  _onChanges() {
    this.formSub$ = this.form.controls.richText.valueChanges
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        map(val => {
          if (this.form.dirty) {
            if (!this.hideMaxLength && this.counterValue > this.textMaxLength) {
              this._translate
                .get('generic_text_too_long')
                .pipe(take(1))
                .subscribe(msg => {
                  this._translate
                    .get('generic_text_too_long2')
                    .pipe(take(1))
                    .subscribe(msg2 => {
                      this._snackbar.open(
                        `${msg} ${this.textMaxLength} ${msg2}`,
                        null,
                        {
                          duration: 3000,
                        }
                      );
                      this.errorMsg = `${msg} ${this.textMaxLength} ${msg2}`;
                      this.error = true;
                      this.ref.detectChanges();
                    });
                });
            } else {
              this.error = false;
              this.sendUpdatedTextEvent();
              this.form.markAsPristine();
              this.ref.detectChanges();
            }
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (this.formSub$) {
      this.formSub$.unsubscribe();
    }
  }

  public editorCreated(q: Quill) {
    this.showEditor = false;
    this.counter = q.getText().length || 0;
    if (this.format === 'html') {
      this.initializeHtmlEditor(q);
    } else {
      this.initializeEditor(q);
    }
    this.showEditor = true;
    this.ref.detectChanges();
  }

  public onContentChange(data: any) {
    this.showEditor = false;
    this.counter = data.text.trim().length;
    this.form.patchValue({
      plainText: data.text.trim(),
    });
    this.form.markAsDirty();
    this.showEditor = true;
    this.ref.detectChanges();
  }

  initializeHtmlEditor(quill: Quill) {
    this.form.patchValue({
      richText: this.richText,
      plainText: this.plainText,
    });
    this.sendUpdatedTextEvent();
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      const ops = [];
      delta.ops.forEach(op => {
        if (op.insert && typeof op.insert === 'string') {
          ops.push({
            insert: op.insert,
          });
        }
      });
      delta.ops = ops;
      return delta;
    });
  }

  initializeEditor(quill: Quill) {
    // change plain text to rich text if rich is not present
    if (
      this.plainText &&
      this.plainText.length &&
      !this.richText &&
      !this.richText.length
    ) {
      this.form.patchValue({
        richText: JSON.stringify(this.initializeText(quill, this.plainText)),
        plainText: this.plainText.trim(),
      });
      this.sendUpdatedTextEvent();
    } else if (!this._tryParseJSON(this.richText)) {
      this.form.patchValue({
        richText: JSON.stringify(this.initializeText(quill, this.richText)),
        plainText: this.richText,
      });
      this.sendUpdatedTextEvent();
    } else {
      this.form.patchValue({
        richText: this.richText,
        plainText: this.plainText,
      });
      this.sendUpdatedTextEvent();
    }
    this.formatOnNewInput(quill);
    // if (this.richText) {
    //     this.form.patchValue({
    //         richText: JSON.stringify(this._convertStringToDelta(this.plainText))
    //     });
    //     this.onRichTextUpdated.emit(this.formValue.richText);
    // }

    // if we need anything else than <p> as quill tag we can change it this way:
    // const Parchment = Quill.import('parchment');
    // const Block = Parchment.query('block');
    // Block.tagName = 'SPAN';
    // Quill.register(Block, true);
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      const ops = [];
      delta.ops.forEach(op => {
        if (op.insert && typeof op.insert === 'string') {
          ops.push({
            insert: op.insert,
          });
        }
      });
      delta.ops = ops;
      return delta;
    });
  }

  private sendUpdatedTextEvent() {
    this.onTextUpdated.emit({
      plain: this.formValue.plainText || '',
      rich: this.formValue.richText,
    });
  }

  formatOnNewInput(quill) {
    switch (this.type) {
      case 'h1':
        this.placeholderClass = `inner placeholder-h1 ${
          this.textarea ? 'textarea__placeholder' : ''
        }`;
        return (this.showEditor = true);
      case 'h2':
        this.placeholderClass = `inner placeholder-h2 ${
          this.textarea ? 'textarea__placeholder' : ''
        }`;
        return (this.showEditor = true);
      case 'h3':
        this.placeholderClass = `inner placeholder-h3 ${
          this.textarea ? 'textarea__placeholder' : ''
        }`;
        return (this.showEditor = true);
      case 'p':
        this.placeholderClass = `inner placeholder-p ${
          this.textarea ? 'textarea__placeholder' : ''
        }`;
        return (this.showEditor = true);
      case 'finn':
        this.placeholderClass = `inner placeholder-p ${
          this.textarea ? 'textarea__placeholder' : ''
        }`;
        return (this.showEditor = true);
    }
  }

  get formValue() {
    return this.form ? this.form.value : { plainText: '', richText: '' };
  }

  private quillGetHTML(inputDelta) {
    if (!inputDelta) {
      return '';
    }
    const delta = this.format === 'html' ? JSON.parse(inputDelta) : inputDelta;
    // another check, better to be safe than sorry
    if (!(delta && delta['ops'] && delta['ops'].length)) {
      return '';
    }
    const tempCont = document.createElement('div');
    new Quill(tempCont).setContents(delta);
    return tempCont.getElementsByClassName('ql-editor')[0].innerHTML;
  }

  private _tryParseJSON(jsonString: string) {
    try {
      const o = JSON.parse(jsonString);

      // Handle non-exception-throwing cases:
      // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
      // but... JSON.parse(null) returns null, and typeof null === "object",
      // so we must check for that, too. Thankfully, null is falsey, so this suffices:
      if (o && typeof o === 'object') {
        return o;
      }
    } catch (e) {}

    return false;
  }

  private initializeText(quill: Quill, text: string) {
    let delta;
    text = text || '';
    switch (this.type) {
      case 'h1':
        delta = quill.setContents([
          { insert: text },
          {
            attributes: { header: 1 },
            insert: '\n',
          },
        ]);
        break;
      case 'h2':
        delta = quill.setContents([
          { insert: text },
          {
            attributes: { header: 2 },
            insert: '\n',
          },
        ]);
        break;
      case 'h3':
        delta = quill.setContents([
          { insert: text },
          {
            attributes: { header: 3 },
            insert: '\n',
          },
        ]);
        break;
      case 'p':
      case 'finn':
        delta = quill.setContents([{ insert: text }, { insert: '\n' }]);
        break;
    }
    return delta;
  }
}
